import React from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Avatar,
  Divider,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { selectAdmin } from '../redux/adminSlice';
import { selectgroups } from '../redux/businessSlice';

const Profile = () => {
  const admin = useSelector(selectAdmin);
  const groups = useSelector(selectgroups);

  if (!admin) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          No user information available
        </Typography>
      </Box>
    );
  }

  // Get business group name
  const getBusinessGroupName = (businessTypeId) => {
    if (!Array.isArray(groups) || !businessTypeId) return 'N/A';
    const businessGroup = groups.find((group) => group._id === businessTypeId);
    return businessGroup?.businessNature || 'N/A';
  };

  // Get permission labels
  const getPermissionLabels = () => {
    if (!admin.permissions) return [];
    const permissions = [];

    // Organization permissions
    if (admin.permissions.deleteOrganization) permissions.push('Delete Organization');
    if (admin.permissions.editOrganization) permissions.push('Edit Organization');
    if (admin.permissions.addOrganization) permissions.push('Add Organization');
    if (admin.permissions.viewAllOrganization) permissions.push('View All Organization');
    if (admin.permissions.viewSpecificCompanies) permissions.push('View Specific Companies');
    if (admin.permissions.viewCountryBusinessTypeOrganization)
      permissions.push('View Country Business Type Organization');
    if (admin.permissions.viewBusinessTypeOrganization)
      permissions.push('View Business Type Organization');

    // User permissions
    if (admin.permissions.showUser) permissions.push('Show User');
    if (admin.permissions.editUser) permissions.push('Edit User');
    if (admin.permissions.deleteUser) permissions.push('Delete User');

    return permissions;
  };

  const permissionLabels = getPermissionLabels();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#003B93' }}>
        User Profile
      </Typography>

      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', pt: 4 }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  mx: 'auto',
                  mb: 2,
                  bgcolor: '#003B93',
                  fontSize: '3rem',
                }}
              >
                {admin.firstName?.[0]?.toUpperCase() || 'U'}
                {admin.lastName?.[0]?.toUpperCase() || ''}
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                {admin.firstName} {admin.lastName}
              </Typography>
              <Chip
                label={admin.userRole || 'User'}
                color={admin.userRole === 'Admin' ? 'primary' : 'default'}
                icon={<AdminPanelSettingsIcon />}
                sx={{ mb: 2 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Details Card */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#003B93' }}>
              Personal Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PersonIcon sx={{ mr: 2, color: '#003B93' }} />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Full Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {admin.firstName} {admin.lastName}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmailIcon sx={{ mr: 2, color: '#003B93' }} />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Email Address
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {admin.email || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BusinessIcon sx={{ mr: 2, color: '#003B93' }} />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Business Group
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getBusinessGroupName(admin.businessType)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationOnIcon sx={{ mr: 2, color: '#003B93' }} />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Country
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {admin.userCountry || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AdminPanelSettingsIcon sx={{ mr: 2, color: '#003B93' }} />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      User Role
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {admin.userRole || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ mr: 2, color: '#003B93' }}>
                    <Typography variant="caption" color="textSecondary">
                      Account Status
                    </Typography>
                    <Chip
                      label={admin.isVerified ? 'Verified' : 'Pending'}
                      color={admin.isVerified ? 'success' : 'warning'}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Permissions Section */}
            {permissionLabels.length > 0 && (
              <>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#003B93' }}>
                  Permissions
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {permissionLabels.map((permission, index) => (
                    <Chip
                      key={index}
                      label={permission}
                      size="small"
                      sx={{
                        bgcolor: '#003B93',
                        color: 'white',
                        '&:hover': {
                          bgcolor: '#002a6b',
                        },
                      }}
                    />
                  ))}
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile;

